import {
  ButtonPrimaryCompact,
  ButtonTertiaryCompact,
  colors2,
  UI_BODY_SM
} from "oolib";
import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import {
  allWidgetsBaseConfs,
  componentsMap,
  defaultWidgetConfigs,
  processRawConfig,
  someReqPropsUnsetFns,
} from "../../CONFIG";
import { TOTAL_COLUMNS, WIDGET_MAX_HEIGHT, MAX_WIDGETS_PER_ROW } from "../../constants";
import { _getStartEndColumns } from "../../utils/_getStartEndColumns";
import { handleDeleteWidget } from "../../utils/handleDeleteWidget";
import { _getItemsAfterThisWidgetInThisRow } from "../../utils/_getItemsAfterThisWidgetInThisRow";
import { useRowHover } from "../../utils/useRowHover";
import { AddWidgetButton } from "../AddWidgetButton";
import {
  WidgetErrorState,
  WidgetLoadingState,
  WidgetUnderConstructionState,
} from "./comps/altStatePlaceholders";
import { DataVizWidgetShell } from "./comps/DataVizWidgetShell";
import { SNAPSHOT_UNAVAILABLE_MARKER } from "../../utils/useShareReport";
import { useReportLandingContext, useWidgetEditing, useReportConfig, useReportFilters, useReportMode, useReportCollab } from "../../ReportLandingContext";
import { ButtonPrimary, ButtonTertiary, ButtonGhost } from "oolib/dist/v2/components/Buttons";
import { PopoverMenu } from "../PopoverMenu";
import { AlertButton } from "../ReportSettingForm";
import { WidgetActions } from "./comps/WidgetActions";
import { actionButtonSize } from "./comps/DataVizWidgetShell/styled";
import { downloadWidgetAsImage } from "../../utils/downloadWidgetAsImage";
import { readVisibilityStateFromProps } from "../WidgetComps/utils/visibilityState";
import { useUpdateLoadedWidgetsTracker } from "../../ReportLandingContext/useUpdateLoadedWidgetsTracker";

export const WidgetRenderer = ({
  widgetConfig: _widgetConfig,
  widgetIdx,
  setUnderEditWidgetId, // This comes from parent with enhanced logic (takes a stable widget id)
  dragHandleRef,
}) => {
  const { loadedWidgetsTracker, snapshotData, isPublicReport, tempWidgetConfig } = useReportLandingContext();
  const { underEditWidgetIdx } = useWidgetEditing();
  const { config, setConfig } = useReportConfig();
  const { activeFilters } = useReportFilters();
  const { readOnly } = useReportMode();
  const { collabActive, reportCollab } = useReportCollab();
  // Per-widget collab lock: when another collaborator holds this widget (lock
  // keyed by its stable id, config.<id>), block editing it and show who has it.
  const widgetLock = collabActive ? reportCollab.locks?.[`config.${_widgetConfig.id}`] : undefined;
  const lockedByOther = !!widgetLock && widgetLock.socketId !== reportCollab.socketId;
  const someReqPropsUnset = someReqPropsUnsetFns[_widgetConfig.comp]
    ? someReqPropsUnsetFns[_widgetConfig.comp](_widgetConfig)
    : false; // if the validator function doesnt exist, then just assume the widgetconfig is valid
  // An unconfigured widget renders WidgetUnderConstructionState — its Component
  // never mounts, so it can never self-report into loadedWidgetsTracker. Report
  // it from here instead: share-prep (useShareReport) and the readOnly
  // sequential-load waterfall both wait on EVERY config index, so one
  // unconfigured widget would otherwise stall them forever. status: undefined
  // makes the hook a no-op for configured widgets (they report themselves).
  // Same for a public snapshot's "unavailable" placeholder: the widget never
  // mounts, so the readOnly waterfall below would wait on it forever and every
  // widget after it would stay a title with no body.
  const isSnapshotUnavailable = !!snapshotData?.[_widgetConfig.id]?.[SNAPSHOT_UNAVAILABLE_MARKER];
  useUpdateLoadedWidgetsTracker({
    status: someReqPropsUnset || isSnapshotUnavailable ? "success" : undefined,
    widgetIdx,
  });
  const widgetConfig = !someReqPropsUnset
    ? processRawConfig[_widgetConfig.comp] //basically if a processor function has been written for this component then use it to process and reshape the config. else do nothing
      ? processRawConfig[_widgetConfig.comp](_widgetConfig)
      : _widgetConfig
    : _widgetConfig;

  const { gridColumn, gridRow, props, comp } = widgetConfig;

  // Visibility state for the metadata tags. Widgets with a processRawConfigFn
  // (bar, pie) already carry it; the rest (heatmap) derive it from their flat
  // config props. Memoised so the shell isn't handed new objects every render.
  const dim1Visibility = useMemo(
    () => props?.dim1Visibility ?? readVisibilityStateFromProps(props, 1),
    [props]
  );
  const dim2Visibility = useMemo(
    () => props?.dim2Visibility ?? readVisibilityStateFromProps(props, 2),
    [props]
  );
  const defaultProps = defaultWidgetConfigs[comp]?.props || {}; //these are pre defined via configs
  const Comp = componentsMap[comp];

  // Public-share replay: this widget's frozen data slice (undefined in live mode, or for
  // static widgets that render from config). A widget that failed during capture is stored
  // as an "unavailable" marker so we render a placeholder instead of the widget.
  const [ startCol, endCol ] = _getStartEndColumns(gridColumn);
  const columnSpan = endCol - startCol

  const getWidgetsInCurrentRow = () => {
    if (startCol !== 1) return 0; // Only count for first widget in row
    
    const itemsAfterIndexes = _getItemsAfterThisWidgetInThisRow({
      widgetIndex: widgetIdx,
      endCol: endCol,
      config,
      TOTAL_COLUMNS,
    });
    
    return 1 + itemsAfterIndexes.length; // Current widget + widgets after it
  };

  const widgetsInCurrentRow = getWidgetsInCurrentRow();
  const isEditing = widgetIdx === underEditWidgetIdx;

  const widgetRowRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    const widgetContainer = widgetRowRef.current?.querySelector(".widgetContainer");
    if (!widgetContainer) return;
    setIsDownloading(true);
    try {
      await downloadWidgetAsImage(
        widgetContainer,
        widgetConfig?.props?.title || "widget",
        comp
      );
    } catch (e) {
      console.error("Widget download failed", e);
    } finally {
      setIsDownloading(false);
    }
  };

  const { handleMouseEnter, handleMouseLeave } = useRowHover({
    widgetIdx,
    startCol,
    endCol,
    config,
  });

  // AI-summary → editable text convert (the TextT button in WidgetActions).
  // Writes through the declared-widget config path: comp flips to
  // WidgetRichText and the rendered summary is baked in as the widget's text,
  // so the widget needs nothing from its AI-summary past after this.
  const handleConvertToText = ({ toWidgetType }) => {
    const summary = tempWidgetConfig?.[widgetIdx]?.summary;

    if (!readOnly) {
      setUnderEditWidgetId(widgetConfig.id);
    }

    setConfig(
      (prev) =>
        prev.map((w) => {
          if (w.id !== widgetConfig.id) return w;
          const updated = JSON.parse(JSON.stringify(w));
          updated.comp = toWidgetType;
          if (summary) updated.props = { ...updated.props, value: summary };
          return updated;
        }),
      widgetConfig.id
    );
  };

  return (
    <Fragment>
      <div
        ref={widgetRowRef}
        style={{
          position: "relative",
          gridColumn,
          // The text widget is as tall as its text; the 170px floor every other
          // widget needs would leave a band of empty card under a short one.
          minHeight: comp === "WidgetRichText" ? undefined : "170px",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
        className="widget-row-container"
        data-widget-index={widgetIdx}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {startCol === 1 && !readOnly && widgetsInCurrentRow < MAX_WIDGETS_PER_ROW && (
          <div data-dnd-hide>
            <AddWidgetButton
              className="add-widget-button"
              {...{
                currentFirstWidgetInRow: widgetConfig,
                widgetIndex: widgetIdx,
                config,
                setConfig,
                setUnderEditWidgetId,
                style: { height: "100%", top: 0 },
              }}
              variant="primary"
            />
          </div>
        )}
        <DataVizWidgetShell
          dragHandleRef={dragHandleRef}
          className={`${widgetConfig.id}-widget-shell`}
          // The per-widget container / wrapper / header overrides (only the RTE/TEXT widget
          // defines them) exist for its rendered content. While required props are unset the
          // shell shows the generic under-construction placeholder, which must look the same
          // as every other widget, so the overrides are skipped in that state.
          style={{
            height: '100%',
            ...(!someReqPropsUnset && readOnly && defaultProps?.readOnly?.container?.style),
            ...(!someReqPropsUnset && !readOnly && defaultProps?.edit?.container?.style)
          }}
          widgetWrapperStyle={{
            ...(!someReqPropsUnset && !readOnly && defaultProps?.edit?.widgetWrapper?.style),
            ...(!someReqPropsUnset && readOnly && defaultProps?.readOnly?.widgetWrapper?.style)
          }}
          disableHeader={!someReqPropsUnset && readOnly && defaultProps?.readOnly?.disableHeader}
          
          isEditing={isEditing}
          widgetConfig={widgetConfig}
          widgetLabel={allWidgetsBaseConfs.find((c) => c.comp === widgetConfig.comp)
            ?.widgetLabel}
          title={
            widgetConfig?.props?.title ||
            allWidgetsBaseConfs.find((c) => c.comp === widgetConfig.comp)
              ?.display
          }
          disableAccordion={true}
          customHeaderRightComp={
            <>
              <div id={`widget-download-${widgetConfig.id}`}>
                <ButtonGhost
                  icon="DownloadSimple"
                  disabled={isDownloading}
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  style={{ height: `${actionButtonSize}rem`, width: `${actionButtonSize}rem`, margin: "-0.4rem 0" }}
                />
              </div>
              {!readOnly && !lockedByOther && <WidgetActions
                {...{
                  widgetConfig,
                  isEditing,
                  setUnderEditWidgetId,
                  widgetIdx,
                  config,
                  setConfig,
                  handleConvertToText
                }}
              />}
            </>
          }
          allActiveFilters={[
            ...activeFilters,
            ...(props?.widgetFilters || []),
            ...(props?.displaySettings || []) // annotation widget stores its filters here
          ]}
          dim1Visibility={dim1Visibility}
          dim2Visibility={dim2Visibility}
          widgetComp={comp}
          breakdownCategoryName={props?.breakdownCategoryName}
          hideMetadataTags={widgetConfig.comp === 'WidgetMetricCard'}
        >
          {isSnapshotUnavailable ? (
            <WidgetErrorState display={"This part of the report is unavailable"} />
          ) : Comp
            /**
             * cuz for the first widget, we obv dont wanna block it, we want it to render right away
             * so basically what we are saying is. if loaded widget tracker has the index of the previous
             * widget, then allow this widget to go ahead and render, else show a loading state.
             *
             * We do this only in readonly mode because, in edit mode, maybe, there are possibilities
             * of sideeffects, since new widgets are being added/deleted/orders are changing
             */
            ?  (readOnly && widgetIdx !== 0 && loadedWidgetsTracker.every(d =>  d.index !== (widgetIdx - 1)))
              ? <WidgetLoadingState/>
              : (
                <Fragment>
                  {someReqPropsUnset ? (
                    <WidgetUnderConstructionState
                      display={
                        allWidgetsBaseConfs.find(
                          (d) => d.comp === widgetConfig.comp
                        )?.display
                      }
                    />
                  ) : (
                    <Comp
                      {...defaultProps}
                      {...props}
                      id={widgetConfig.id}
                      widgetIdx={widgetIdx}
                      style={{
                        maxHeight: `${WIDGET_MAX_HEIGHT}px`,
                      }}
                      activeFilters={activeFilters}
                      columnSpan={columnSpan}
                      // Public-share replay: when present, the widget renders this frozen
                      // data and skips its own fetch. Undefined in normal (live) mode.
                      prefetchedData={snapshotData ? snapshotData[widgetConfig.id] : undefined}
                      // True only on the public "share for all to view" page — gates
                      // public-specific UI (references, AI-summary/regenerate controls, etc.).
                      isPublicReport={isPublicReport}
                    />
                  )}
                </Fragment>
          ) : (
            <WidgetErrorState display={"Invalid Component"} />
          )}
        </DataVizWidgetShell>

        {lockedByOther && (
          <div
            className="widget-collab-lock-overlay"
            // Swallow clicks so the locked widget can't be entered / dragged.
            onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onMouseDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              background: "rgba(240,240,240,0.55)",
              cursor: "not-allowed",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              padding: "0.8rem",
            }}
          >
            <div style={{ background: colors2.black, borderRadius: "0.4rem", padding: "0.2rem 0.8rem" }}>
              <UI_BODY_SM color={colors2.white}>
                {`${widgetLock?.userName || "Someone"} is editing…`}
              </UI_BODY_SM>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};